import { useId, useState } from "react";
import type { StatsMetric, StatsPoint, StatsScale } from "@shared";
import { formatBucket } from "./statsApi";

/**
 * Graphiques de la page de statistiques, en SVG écrit à la main.
 *
 * POURQUOI PAS UNE BIBLIOTHÈQUE : il y a ici deux formes — une courbe et une
 * barre horizontale. Une bibliothèque de graphiques pèserait plus que tout le
 * reste de cette page, pour des réglages qu'on contredirait ensuite un par un.
 *
 * LA PALETTE A ÉTÉ VALIDÉE, PAS CHOISIE À L'ŒIL. Les cinq teintes passent les
 * contrôles de luminosité, de saturation, de séparation daltonienne et de
 * contraste sur le fond #18224a. Deux conséquences à ne pas défaire :
 *
 *  1. L'ORDRE EST FIGÉ. Les teintes voisines sont séparées au sens du
 *     daltonisme ; permuter deux mesures casse cette garantie. Vérifié : une
 *     permutation testée faisait chuter l'écart à 1,4 au lieu de 7,4.
 *  2. La paire bleu/magenta reste dans la bande basse (écart 7,4). Elle n'est
 *     admissible QU'AVEC un second encodage — d'où le trait pointillé propre à
 *     chaque courbe, repris dans la légende. Ne pas retirer les pointillés en
 *     les croyant décoratifs.
 */
export const COULEURS: Record<StatsMetric, string> = {
  users: "#189a85",
  expedition1: "#a87f10",
  expedition2: "#5286e6",
  mission: "#c75bbf",
  contest: "#6d9926",
};

/** Second encodage, obligatoire : la couleur seule ne suffit pas. */
export const TIRETS: Record<StatsMetric, string> = {
  users: "",
  expedition1: "6 3",
  expedition2: "2 3",
  mission: "9 3 2 3",
  contest: "1 4",
};

export const LIBELLES: Record<StatsMetric, string> = {
  users: "Comptes créés",
  expedition1: "Expédition 1 complétée",
  expedition2: "Expédition 2 complétée",
  mission: "Mission complétée",
  contest: "Participation au concours",
};

interface CourbeProps {
  points: StatsPoint[];
  scale: StatsScale;
  /** Mesures affichées, dans l'ordre figé. */
  mesures: StatsMetric[];
}

/**
 * Courbe des mesures dans le temps.
 *
 * UN SEUL AXE des ordonnées : toutes les mesures comptent des événements, elles
 * partagent donc l'échelle. Jamais deux axes — c'est la faute qui fait dire
 * n'importe quoi à un graphique.
 */
export function Courbe({ points, scale, mesures }: CourbeProps) {
  const [survol, setSurvol] = useState<number | null>(null);
  const id = useId();

  if (points.length === 0) {
    return <p className="stats__vide">Aucune donnée sur cette période.</p>;
  }

  const L = 720;
  const H = 260;
  const marge = { haut: 16, droite: 16, bas: 34, gauche: 34 };
  const largeur = L - marge.gauche - marge.droite;
  const hauteur = H - marge.haut - marge.bas;

  const maxi = Math.max(1, ...points.flatMap((p) => mesures.map((m) => p.counts[m])));
  // Graduations entières : compter des jeunes en demi-personnes n'a pas de sens.
  const pas = Math.max(1, Math.ceil(maxi / 4));
  const plafond = pas * Math.ceil(maxi / pas);

  const x = (i: number) =>
    marge.gauche + (points.length === 1 ? largeur / 2 : (i / (points.length - 1)) * largeur);
  const y = (v: number) => marge.haut + hauteur - (v / plafond) * hauteur;

  const graduations = Array.from({ length: plafond / pas + 1 }, (_, k) => k * pas);
  // Au plus une étiquette sur six : au-delà, elles se chevauchent.
  const saut = Math.max(1, Math.ceil(points.length / 6));

  return (
    <div className="stats__graphique">
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-labelledby={`${id}-titre`}>
        <title id={`${id}-titre`}>
          Évolution de {mesures.map((m) => LIBELLES[m]).join(", ")} dans le temps
        </title>

        {/* Grille en retrait : elle situe, elle ne se regarde pas. */}
        {graduations.map((g) => (
          <g key={g}>
            <line
              x1={marge.gauche}
              x2={L - marge.droite}
              y1={y(g)}
              y2={y(g)}
              className="stats__grille"
            />
            <text x={marge.gauche - 8} y={y(g) + 4} className="stats__graduation">
              {g}
            </text>
          </g>
        ))}

        {points.map((p, i) =>
          i % saut === 0 ? (
            <text key={p.bucket} x={x(i)} y={H - 12} className="stats__abscisse">
              {formatBucket(p.bucket, scale)}
            </text>
          ) : null,
        )}

        {mesures.map((m) => (
          <polyline
            key={m}
            className="stats__trace"
            points={points.map((p, i) => `${x(i)},${y(p.counts[m])}`).join(" ")}
            stroke={COULEURS[m]}
            strokeDasharray={TIRETS[m] || undefined}
          />
        ))}

        {/* Marqueurs quand les points sont peu nombreux.
            Ce n'est pas décoratif : une polyligne d'UN SEUL point ne dessine
            RIEN, et le graphique apparaissait vide le jour où toute la fête
            tenait dans un seul intervalle. Les marqueurs portent aussi la
            valeur quand il n'y a pas de ligne à suivre. */}
        {points.length <= 12 &&
          mesures.map((m) =>
            points.map((p, i) => (
              <circle
                key={`${m}-${p.bucket}`}
                cx={x(i)}
                cy={y(p.counts[m])}
                r="4"
                fill={COULEURS[m]}
                className="stats__point"
              />
            )),
          )}

        {/* Repère de survol : une seule ligne verticale, et les valeurs de
            toutes les mesures à cette date — pas un nombre sur chaque point. */}
        {survol !== null && (
          <>
            <line
              x1={x(survol)}
              x2={x(survol)}
              y1={marge.haut}
              y2={marge.haut + hauteur}
              className="stats__repere"
            />
            {mesures.map((m) => (
              <circle
                key={m}
                cx={x(survol)}
                cy={y(points[survol]!.counts[m])}
                r="4.5"
                fill={COULEURS[m]}
                className="stats__point"
              />
            ))}
          </>
        )}

        {/* Zones de survol : plus larges que les points, pour être atteignables. */}
        {points.map((p, i) => (
          <rect
            key={p.bucket}
            x={x(i) - largeur / Math.max(1, points.length * 2)}
            y={marge.haut}
            width={Math.max(12, largeur / Math.max(1, points.length))}
            height={hauteur}
            fill="transparent"
            onMouseEnter={() => {
              setSurvol(i);
            }}
            onMouseLeave={() => {
              setSurvol(null);
            }}
          />
        ))}
      </svg>

      {survol !== null && (
        <div className="stats__infobulle">
          <strong>{formatBucket(points[survol]!.bucket, scale)}</strong>
          {mesures.map((m) => (
            <span key={m}>
              <span className="stats__puce" style={{ background: COULEURS[m] }} />
              {LIBELLES[m]} : {points[survol]!.counts[m]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface BarresProps {
  titre: string;
  /** Du plus parlant au moins parlant : l'ordre est décidé par l'appelant. */
  lignes: Array<{ nom: string; valeur: number; note?: string }>;
  /** Unité affichée à côté de la valeur. */
  unite?: string;
  couleur?: string;
}

/**
 * Barres horizontales pour un classement.
 *
 * Une SEULE couleur : ces barres comparent des grandeurs d'une même nature, pas
 * des catégories. Les teindre différemment ferait croire à une distinction qui
 * n'existe pas.
 */
export function Barres({ titre, lignes, unite = "", couleur = "#5286e6" }: BarresProps) {
  const maxi = Math.max(1, ...lignes.map((l) => l.valeur));

  return (
    <section className="stats__bloc">
      <h3 className="stats__soustitre">{titre}</h3>
      {lignes.length === 0 ? (
        <p className="stats__vide">Aucune donnée.</p>
      ) : (
        <ul className="barres">
          {lignes.map((l) => (
            <li className="barres__ligne" key={l.nom}>
              <span className="barres__nom">{l.nom}</span>
              <span className="barres__piste">
                {/* ZÉRO NE DESSINE RIEN. Un plancher de largeur, même minime,
                    laissait un trait de couleur sur les leurres jamais cochés
                    et leur donnait l'air d'avoir une valeur. */}
                {l.valeur > 0 && (
                  <span
                    className="barres__barre"
                    style={{
                      width: `${Math.max(1.5, (l.valeur / maxi) * 100)}%`,
                      background: couleur,
                    }}
                  />
                )}
              </span>
              <span className="barres__valeur">
                {l.valeur}
                {unite}
                {l.note !== undefined && <em className="barres__note"> {l.note}</em>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
